import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isAuthenticated, api, notifyAuthChange } from './api.js';

interface AuthState {
  /** Флаг авторизации (JWT в httpOnly cookie, проверяем по CSRF cookie). */
  authenticated: boolean;
  /** ID устройства из ответа /auth/totp/login. */
  deviceId: string | null;
  /** ID пользователя из ответа /auth/totp/login. */
  userId: string | null;
  /** Устанавливает состояние после успешного login/activate. */
  setAuth: (deviceId: string, userId: string) => void;
  /** Logout: очищает cookies через /auth/logout и сбрасывает состояние. */
  logout: () => Promise<void>;
  /** Синхронизирует состояние с cookies (вызывать при загрузке). */
  sync: () => void;
}

// Проверяем состояние при инициализации
const initial = {
  authenticated: isAuthenticated(),
  deviceId: null as string | null,
  userId: null as string | null,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initial,
      setAuth: (deviceId, userId) => {
        set({ authenticated: true, deviceId, userId });
      },
      logout: async () => {
        try {
          // Сервер очистит cookies
          await api.post('/api/v1/auth/logout');
        } catch {
          // Игнорируем ошибки (возможно, уже разлогинен)
        }
        set({ authenticated: false, deviceId: null, userId: null });
        notifyAuthChange(false);
      },
      sync: () => {
        const auth = isAuthenticated();
        if (!auth && get().authenticated) {
          // Cookie исчезла — разлогиниваем
          set({ authenticated: false, deviceId: null, userId: null });
        } else if (auth && !get().authenticated) {
          // Cookie появилась (маловероятно) — обновляем флаг
          set({ authenticated: true });
        }
      },
    }),
    {
      name: 'cr-auth-store',
      // Персистим только deviceId/userId (JWT в cookie)
      partialize: (state) => ({
        deviceId: state.deviceId,
        userId: state.userId,
      }),
    },
  ),
);

// Синхронизируем при событии auth-change
window.addEventListener('cr:auth', () => {
  useAuthStore.getState().sync();
});

// Синхронизируем при фокусе (cookie могла истечь пока вкладка в фоне)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    useAuthStore.getState().sync();
  }
});
